import React, { useState } from "react";
import { submitNetlifyForm } from "./netlifyForms";

/**
 * Shown in place of the pickup form while bookings are paused
 * (REACT_APP_SITE_MODE = "bookings-paused" or "maintenance").
 * Collects a waitlist into the Netlify form "waitlist" — captured in the
 * dashboard and emailed to the team like every other form.
 * No router dependencies, so the maintenance page can use it too.
 */

const PickupWaitlist: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    const f = new FormData(e.currentTarget);
    const str = (k: string) => String(f.get(k) || "");
    try {
      await submitNetlifyForm("waitlist", {
        "bot-field": str("bot-field"),
        name: str("name"),
        email: str("email"),
        zip: str("zip"),
        items: str("items"),
      });
      setSent(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ee-waitlist">
      <div className="ee-pickup-hero">
        <div className="ee-section-label">Pickups</div>
        <h1>Bookings open soon.</h1>
        <p>
          We’re getting our routes, van and team ready. Leave your info and you’ll be the first to know
          the day pickups open in your neighborhood — no spam, ever.
        </p>
      </div>

      <div className="ee-pickup-card ee-waitlist-card">
        {sent ? (
          <div className="ee-waitlist-done">
            <div className="ee-waitlist-check">🎉</div>
            <h2>You’re on the list!</h2>
            <p>We’ll email you the moment pickups open in your area. Thank you for the patience — it means a lot.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
              <label>Don’t fill this out: <input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
            </p>
            <div className="ee-field-row">
              <div className="ee-field">
                <label>Name</label>
                <input type="text" name="name" required className="ee-input" placeholder="Jordan Rivera" />
              </div>
              <div className="ee-field">
                <label>Email</label>
                <input type="email" name="email" required className="ee-input" placeholder="you@email.com" />
              </div>
            </div>
            <div className="ee-field-row">
              <div className="ee-field">
                <label>ZIP code</label>
                <input type="text" name="zip" inputMode="numeric" pattern="[0-9]{5}" className="ee-input" placeholder="11211" />
              </div>
              <div className="ee-field">
                <label>What do you have?</label>
                <select name="items" className="ee-input" defaultValue="Electronics / E-Waste">
                  <option>Electronics / E-Waste</option>
                  <option>Clothing &amp; Textiles</option>
                  <option>Furniture / Bulk</option>
                  <option>Mixed / Multiple Items</option>
                </select>
              </div>
            </div>
            {error && (
              <p className="ee-date-error" role="alert">
                Hmm, that didn’t go through — please try again, or email us at everythingeconyc@gmail.com.
              </p>
            )}
            <button type="submit" disabled={sending} className="ee-btn ee-btn-green ee-btn-full">
              {sending ? "Saving…" : "📬 Put me first in line"}
            </button>
            <p className="ee-form-reassure">Free · No account needed · We only email you when pickups open</p>
          </form>
        )}
      </div>

      <p className="ee-waitlist-help">
        Something urgent? Email <a href="mailto:everythingeconyc@gmail.com">everythingeconyc@gmail.com</a> and we’ll do our best.
      </p>
    </div>
  );
};

export default PickupWaitlist;
